import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/components/ui/button';
import { IconBook, IconChat, IconExpand, IconX } from '@/components/ui/icons';
import { clearHelpChat, HelpChat } from './HelpChat';

/** Help lives in the top bar, outside every workspace/composer. */
export function HelpLauncher({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname.startsWith('/help/chat')) return null;
  return (
    <button
      id="help-launcher"
      type="button"
      onClick={() => onOpenChange(!open)}
      aria-label={t(open ? 'help.chatClose' : 'help.chatOpen')}
      title={t('help.chatOpen')}
      aria-expanded={open}
      aria-controls={open ? 'help-panel' : undefined}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-fg-2 hover:bg-hover hover:text-fg"
    >
      <IconChat size={18} />
    </button>
  );
}

/** A layout sibling, never an overlay over primary chat controls. */
export function HelpPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [gen, setGen] = useState(0);
  const panel = useRef<HTMLElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () => {
      if (viewport && panel.current) {
        setMaxHeight(
          Math.max(0, viewport.height + viewport.offsetTop - panel.current.getBoundingClientRect().top),
        );
      }
    };
    resize();
    viewport?.addEventListener('resize', resize);
    viewport?.addEventListener('scroll', resize);
    window.addEventListener('resize', resize);
    return () => {
      viewport?.removeEventListener('resize', resize);
      viewport?.removeEventListener('scroll', resize);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return (
    <section
      ref={panel}
      id="help-panel"
      style={{
        maxHeight,
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.stopPropagation();
          onClose();
        }
      }}
      aria-label={t('help.chatTitle')}
      className="flex min-h-0 w-full shrink-0 flex-col overflow-hidden border-l border-border bg-bg lg:w-[24rem] lg:max-w-[40%]"
    >
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <IconBook size={16} className="text-muted" />
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{t('help.chatTitle')}</h2>
        <button
          type="button"
          onClick={() => {
            clearHelpChat();
            setGen((g) => g + 1);
          }}
          className="rounded-md px-2 py-0.5 text-xs text-muted hover:bg-hover hover:text-fg"
        >
          {t('help.chatNew')}
        </button>
        <Link
          to="/help/chat"
          onClick={() => onClose()}
          aria-label={t('help.chatFull')}
          title={t('help.chatFull')}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-hover hover:text-fg"
        >
          <IconExpand size={15} />
        </Link>
        <IconButton label={t('help.chatClose')} onClick={() => onClose()}>
          <IconX size={15} />
        </IconButton>
      </header>
      <HelpChat key={gen} autoFocus className="flex-1" />
    </section>
  );
}
